import * as React from 'react';
import {IDismissedTimeline} from 'features/channelTimelines/channelTimelinesApi';

interface UseDismissedNotification {
  dismissed: IDismissedTimeline[];
  showModal: boolean;
  setShowModal: (val: boolean) => void;
  onDismissedChange: (dismissed: IDismissedTimeline[]) => void;
}

// Module-level so the "already shown" signature survives EditChannel remounting on every tab
// switch (see AppRouter's per-route keys + fullscreen Program wrapper). A useRef would reset on
// remount and re-alert on every return to the Program tab. Keyed by channelId; resets on reload.
const shownSignatures = new Map<string, string>();

// Tracks slots whose episode was deleted and decides when to surface the gap notification.
// The modal opens once per distinct set of dismissed episodes (so re-entering or refetching the
// draft doesn't re-alert for the same gaps), and resets once there are no dismissed slots left.
export function useDismissedNotification(channelId: string): UseDismissedNotification {
  const [dismissed, setDismissed] = React.useState<IDismissedTimeline[]>([]);
  const [showModal, setShowModal] = React.useState(false);

  const onDismissedChange = React.useCallback(
    (next: IDismissedTimeline[]) => {
      setDismissed(next);

      if (next.length === 0) {
        shownSignatures.delete(channelId);
        setShowModal(false);
        return;
      }

      const signature = next
        .map(d => d.episodeId)
        .sort()
        .join(',');
      if (signature !== shownSignatures.get(channelId)) {
        shownSignatures.set(channelId, signature);
        setShowModal(true);
      }
    },
    [channelId],
  );

  return {dismissed, showModal, setShowModal, onDismissedChange};
}
